import { useCallback } from "react";
import {
  Box,
  MenuItem,
  Select as MuiSelect,
  TextField as MuiTextField,
} from "@mui/material";
import type { ProcessedFormField } from "@/types/api/formSchema";
import { PHONE_RULES } from "../utils/schemaUtils";

type PhoneValue = {
  number: string;
  country_code: string;
};

type Props = {
  field: ProcessedFormField;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  onBlur: () => void;
  name: string;
};

export const COUNTRY_CODES = [
  { code: "+91", label: "IN (+91)" },
  { code: "+65", label: "SG (+65)" },
  { code: "+1", label: "US (+1)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+971", label: "AE (+971)" },
  { code: "+61", label: "AU (+61)" },
  { code: "+49", label: "DE (+49)" },
  { code: "+81", label: "JP (+81)" },
  { code: "+86", label: "CN (+86)" },
] as const;

const DEFAULT_MAX_LENGTH = 15;

export function PhoneField({
  field: schemaField,
  value,
  onChange,
  onBlur,
  name,
}: Props) {
  const phoneValue: PhoneValue = value ?? { number: "", country_code: "+91" };
  const maxLength =
    PHONE_RULES[phoneValue.country_code]?.length ?? DEFAULT_MAX_LENGTH;

  const handleCodeChange = useCallback(
    (code: string) => {
      const newMax = PHONE_RULES[code]?.length ?? DEFAULT_MAX_LENGTH;
      const trimmed = phoneValue.number.slice(0, newMax);
      onChange({ country_code: code, number: trimmed });
    },
    [phoneValue.number, onChange],
  );

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const cleaned = e.target.value.replace(/\D/g, "").slice(0, maxLength);
      onChange({ country_code: phoneValue.country_code, number: cleaned });
    },
    [onChange, maxLength, phoneValue.country_code],
  );

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <MuiSelect
        id={`${name}-code`}
        value={phoneValue.country_code}
        disabled={!!schemaField.disabled}
        onChange={(e) => handleCodeChange(e.target.value as string)}
        size="small"
        variant="outlined"
        sx={{ width: 130, flexShrink: 0 }}
      >
        {COUNTRY_CODES.map((cc) => (
          <MenuItem key={cc.code} value={cc.code}>
            {cc.label}
          </MenuItem>
        ))}
      </MuiSelect>

      <MuiTextField
        id={name}
        name={name}
        type="tel"
        value={phoneValue.number}
        placeholder="Phone number"
        disabled={!!schemaField.disabled}
        onChange={handleNumberChange}
        onBlur={onBlur}
        inputProps={{
          inputMode: "numeric",
          maxLength,
          autoComplete: name,
        }}
        fullWidth
        size="small"
        variant="outlined"
      />
    </Box>
  );
}
